import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { getSentryServiceInstance } from '../../services/sentry/sentry.service';

const PERMISSION_DENIED_CODE = 'permission-denied';
const PERMISSION_DENIED_MESSAGE = 'Missing or insufficient permissions';

/** Minimal shape of a Firebase/Firestore permission-denied error. */
interface FirebasePermissionError {
  code?: string;
  message?: string;
}

/** Returns true when the error is a Firestore/Firebase permission-denied error. */
export function isFirebasePermissionError(error: FirebasePermissionError | null | undefined): boolean {
  return (
    error?.code === PERMISSION_DENIED_CODE ||
    (typeof error?.message === 'string' && error.message.includes(PERMISSION_DENIED_MESSAGE))
  );
}

/**
 * Handles a Firebase permission-denied error by capturing it in Sentry and
 * surfacing a user-facing snack bar message so the failure is never silent.
 *
 * @param error           - The error thrown by a Firebase/Firestore operation.
 * @param snackBar        - Angular Material snack bar for user notification.
 * @param isAuthenticated - Whether the user was authenticated at the time of
 *                          the error; passed as Sentry context to distinguish
 *                          token-expiry failures from misconfigured security rules.
 */
export function handleFirebasePermissionError(
  error: FirebasePermissionError | null | undefined,
  snackBar: MatSnackBar,
  isAuthenticated: boolean = false
): void {
  if (!isFirebasePermissionError(error)) {
    return;
  }

  getSentryServiceInstance().captureFirebasePermissionError(error, isAuthenticated);

  snackBar.open('Your session has expired. Please sign in again.', 'Dismiss', {
    duration: 5000,
  });
}
